import React, { useEffect, useState, useCallback } from 'react';

import { Link } from 'react-router-dom';

import { Divider, Modal } from '@material-ui/core';

import { FiEdit3, FiTrash2 } from 'react-icons/fi';

import api from '../../services/api';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import Toggle from '../../newComponents/Toggle';
import Button from '../../newComponents/Button';

import * as S from './styles';

export interface GrupoTributario {
  id: number;
  descricao: string;
  cst_csosn?: string | null;
  aliquota_icms?: string | number | null;
  ativo: boolean;
}

const GruposTributarios: React.FC = () => {
  const [grupos, setGrupos] = useState<GrupoTributario[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [criandoPadrao, setCriandoPadrao] = useState(false);

  const { user } = useAuth();
  const { addToast } = useToast();

  const loadGrupos = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get(
        `companies/${user.empresa_id}/grupos-tributarios`,
      );
      setGrupos(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar grupos tributários',
      });
    }
  }, [user.empresa_id, addToast]);

  useEffect(() => {
    loadGrupos();
  }, [loadGrupos]);

  const handleChangeAtivo = useCallback(
    async (grupoId: number, ativo: boolean) => {
      try {
        const response = await api.patch(
          `companies/${user.empresa_id}/grupos-tributarios/${grupoId}`,
          { ativo },
        );

        addToast({
          type: 'success',
          title: response.data.ativo ? 'Grupo ativado' : 'Grupo desativado',
        });

        setGrupos((current) =>
          current.map((grupo) =>
            grupo.id === grupoId
              ? { ...grupo, ativo: response.data.ativo }
              : grupo,
          ),
        );
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao atualizar status do grupo',
        });
      }
    },
    [user.empresa_id, addToast],
  );

  /**
   * Cria os grupos padrão do regime da empresa. O backend é idempotente por
   * descrição: rodar de novo não duplica nem sobrescreve o que já foi ajustado.
   */
  const handleCriarPadrao = useCallback(async () => {
    setCriandoPadrao(true);
    try {
      const { data } = await api.post(
        `companies/${user.empresa_id}/grupos-tributarios/padrao`,
      );

      const criados = data?.criados?.length ?? 0;

      addToast({
        type: criados > 0 ? 'success' : 'info',
        title:
          criados > 0
            ? `${criados} grupo(s) padrão criado(s)`
            : 'Os grupos padrão já estavam cadastrados',
        description:
          criados > 0
            ? 'Revise as alíquotas: elas variam por UF, produto e enquadramento.'
            : undefined,
      });

      await loadGrupos();
    } catch (err) {
      addToast({ type: 'error', title: 'Erro ao criar os grupos padrão' });
    } finally {
      setCriandoPadrao(false);
    }
  }, [user.empresa_id, addToast, loadGrupos]);

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;

    try {
      await api.delete(
        `companies/${user.empresa_id}/grupos-tributarios/${deleteId}`,
      );

      addToast({ type: 'success', title: 'Grupo excluído' });
      setGrupos((current) => current.filter((grupo) => grupo.id !== deleteId));
    } catch (err) {
      addToast({ type: 'error', title: 'Erro ao excluir grupo' });
    } finally {
      setDeleteId(null);
    }
  }, [deleteId, user.empresa_id, addToast]);

  return (
    <S.Container>
      <div className="header">
        <h2>Grupos de Regras Tributárias</h2>
        <p>
          Cadastre os grupos de tributação (ICMS / PIS / COFINS) reutilizados
          pelas categorias fiscais.
        </p>
      </div>

      <Divider />

      <div className="create_user">
        <div className="padrao">
          <Button
            full
            variant="outlined"
            type="button"
            loading={criandoPadrao}
            disabled={criandoPadrao}
            onClick={handleCriarPadrao}
          >
            Criar grupos padrão
          </Button>
        </div>
        <Link to="/grupos-tributarios/new">Adicionar Grupo</Link>
      </div>

      <S.ContainerList>
        <S.CustomTable>
          <S.TableHeader>
            <span>Descrição</span>
            <span>CST/CSOSN</span>
            <span>Alíq. ICMS</span>
            <span>Editar</span>
            <span>Excluir</span>
            <span>Status</span>
          </S.TableHeader>
          <S.TableBody>
            {grupos.map((grupo) => (
              <S.TableRow key={grupo.id}>
                <S.TableCellRow>
                  <p>{grupo.descricao}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{grupo.cst_csosn || '-'}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{grupo.aliquota_icms ?? '-'}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <Link to={`/grupos-tributarios/edit/${grupo.id}`}>
                    <FiEdit3 size={20} />
                  </Link>
                </S.TableCellRow>
                <S.TableCellRow>
                  <button type="button" onClick={() => setDeleteId(grupo.id)}>
                    <FiTrash2 size={20} />
                  </button>
                </S.TableCellRow>
                <S.TableCellRow>
                  <Toggle
                    checked={grupo.ativo}
                    firstText="Inativo"
                    secondText="Ativo"
                    onToggle={(value) => handleChangeAtivo(grupo.id, value)}
                  />
                </S.TableCellRow>
              </S.TableRow>
            ))}
          </S.TableBody>
        </S.CustomTable>
      </S.ContainerList>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
        <S.ModalContent>
          <h1>Excluir grupo tributário?</h1>
          <p>Esta ação não pode ser desfeita.</p>
          <div className="modal-actions">
            <Button
              variant="outlined"
              full
              type="button"
              onClick={() => setDeleteId(null)}
            >
              Cancelar
            </Button>
            <Button full type="button" onClick={handleDelete}>
              Excluir
            </Button>
          </div>
        </S.ModalContent>
      </Modal>
    </S.Container>
  );
};

export default GruposTributarios;
